import { savePageOrder } from '../services/trackedSaves';
import {
  fetchBatchImagesByBookId,
  isStagingUploadEnabled,
} from '../services/mediaService';
import { loadPendingImages } from './pendingImageStorage';
import { mergeDbAndPending, preserveInMemoryGridUploads } from './imageDedup';
import { getMediaMongoId, isMongoMediaId } from './placeOrderImageUtils';
import { mapDbInteriorToGridImage } from './stagingBookImageUtils';

export { isMongoMediaId };

/** Interior pages only — ordering 0..n-1 for rows saved in Mongo. */
export const buildInteriorOrderingPayload = (orderedInteriorImages) =>
  orderedInteriorImages
    .map((item, index) => {
      const id = getMediaMongoId(item) || item._id || item.id;
      if (!isMongoMediaId(String(id))) return null;
      return { id: String(id), ordering: index };
    })
    .filter(Boolean);

/** Persist the latest interior page order to the database. */
export const persistInteriorPageOrder = async (orderedInteriorImages) => {
  const ordering_data = buildInteriorOrderingPayload(orderedInteriorImages);
  if (ordering_data.length > 0) {
    // Reported on the "Saved" pill, retried if it fails, kept on the device until it lands.
    await savePageOrder({ ordering_data });
  }
  return ordering_data;
};

export const sortByPageOrdering = (images) =>
  [...images].sort((a, b) => {
    const ao = a.ordering ?? a.gridOrdering ?? a.index ?? 999;
    const bo = b.ordering ?? b.gridOrdering ?? b.index ?? 999;
    return ao - bo;
  });

/**
 * Load interior pages from DB + pending localStorage, sorted by saved order.
 * Pass `rows` (a batch-wise-list result the caller just fetched) to skip a second request.
 */
export const fetchOrderedInteriorImages = async (bookId, { liveImages, rows } = {}) => {
  if (!bookId) return [];
  const allRows = rows || (await fetchBatchImagesByBookId(bookId))?.docs?.data;
  const dbRows = allRows?.filter((image) => !image.isCover) || [];

  const pendingImages = loadPendingImages(bookId);
  const useStaging = await isStagingUploadEnabled();

  const dbImages = useStaging
    ? dbRows.map(mapDbInteriorToGridImage).filter(Boolean)
    : dbRows;

  let merged = mergeDbAndPending(dbImages, pendingImages, bookId);

  if (useStaging) {
    merged = sortByPageOrdering(merged);
  }

  if (liveImages?.length) {
    merged = preserveInMemoryGridUploads(merged, liveImages, sortByPageOrdering);
  }

  return merged;
};

/**
 * Keep Redux sketch.images aligned with Preview Book interior order.
 */
export const mapPreviewPagesToSketchImages = (orderedInteriorPages, sketchImages) => {
  const byId = new Map(
    (sketchImages || []).map((img) => [String(img._id || img.id), img]),
  );

  return orderedInteriorPages.map((page, index) => {
    const id = page.id ?? page._id;
    const existing = id ? byId.get(String(id)) : null;
    if (existing) {
      return { ...existing, ordering: index };
    }
    return {
      _id: id,
      id,
      url: page.originalUrl,
      originalUrl: page.originalUrl,
      AIUrl: page.sketchUrl,
      publicId: page.publicId,
      ordering: index,
    };
  });
};
